/**
 * Methods for interacting with ToDesktop application's menu.
 *
 * @remarks
 * This package exposes a number of methods for interacting with ToDesktop
 * application's menu.

 * @experimental
 * @public
 * @packageDocumentation
 */
import { invoke } from "./invoke.js";
import { createOnAndOffNamespaceListeners, } from "./utils.js";
/**
 * The current absolute position of the mouse pointer.
 *
 * @returns The current absolute position of the mouse pointer. Note: DIP point, not a screen physical point.
 * @public
 */
export async function getCursorScreenPoint() {
    return invoke({
        namespace: "screen",
        method: "getCursorScreenPoint",
        args: [],
    });
}
/**
 * @returns The primary display.
 * @public
 */
export async function getPrimaryDisplay() {
    return invoke({
        namespace: "screen",
        method: "getPrimaryDisplay",
        args: [],
    });
}
/**
 * @returns An array of displays that are currently available.
 * @public
 */
export async function getAllDisplays() {
    return invoke({
        namespace: "screen",
        method: "getAllDisplays",
        args: [],
    });
}
/**
 * @returns The display nearest the specified point.
 * @public
 */
export async function getDisplayNearestPoint(point) {
    return invoke({
        namespace: "screen",
        method: "getDisplayNearestPoint",
        args: [point],
    });
}
/**
 * @returns The display that most closely intersects the provided bounds.
 * @public
 */
export async function getDisplayMatching(rect) {
    return invoke({
        namespace: "screen",
        method: "getDisplayMatching",
        args: [rect],
    });
}
/**
 * Converts a screen physical point to a screen DIP point.
 * The DPI scale is performed relative to the display containing the physical point.
 *
 * @public
 */
export async function screenToDipPoint(point) {
    return invoke({
        namespace: "screen",
        method: "screenToDipPoint",
        args: [point],
    });
}
/**
 * Converts a screen DIP point to a screen physical point.
 * The DPI scale is performed relative to the display containing the DIP point.
 *
 * @public
 */
export async function dipToScreenPoint(point) {
    return invoke({
        namespace: "screen",
        method: "dipToScreenPoint",
        args: [point],
    });
}
/**
 * Converts a screen physical rect to a screen DIP rect.
 * The DPI scale is performed relative to the display nearest to `window`.
 * If `window` is null, scaling will be performed to the display nearest to `rect`.
 *
 * @returns
 * @public
 */
export async function screenToDipRect(point) {
    return invoke({
        namespace: "screen",
        method: "dipToScreenPoint",
        args: [point],
    });
}
/**
 * Converts a screen DIP rect to a screen physical rect.
 * The DPI scale is performed relative to the display nearest to `window`.
 * If `window` is null, scaling will be performed to the display nearest to `rect`.
 *
 * @returns
 * @public
 */
export async function dipToScreenRect(point) {
    return invoke({
        namespace: "screen",
        method: "dipToScreenPoint",
        args: [point],
    });
}
const listeners = createOnAndOffNamespaceListeners("screen");
export const on = listeners.on;
/**
 * Unsubscribes all menu objects from the event name.
 *
 * @param options - ref: Reference for the Menu; eventName: The event name to unsubscribe from;
 * @returns Reference for the menu.
 * @public
 */
export const removeAllListeners = listeners.removeAllListeners;
